import { useCallback, useMemo, useState } from 'react'

export interface UseClientPaginationOptions {
  defaultRowsPerPage?: number
}

export interface UseClientPaginationResult<T> {
  page: number
  rowsPerPage: number
  paginated: T[]
  setPage: (page: number) => void
  setRowsPerPage: (rowsPerPage: number) => void
  resetPage: () => void
}

/**
 * Stateful client-side pagination for a fully-loaded array of rows. Slices
 * `rows` into the current page and exposes pagination controls. Callers are
 * responsible for calling `resetPage()` when their upstream filter changes.
 */
export function useClientPagination<T>(rows: T[], opts?: UseClientPaginationOptions): UseClientPaginationResult<T> {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(opts?.defaultRowsPerPage ?? 25)

  const paginated = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  )

  const resetPage = useCallback(() => setPage(0), [])

  return { page, rowsPerPage, paginated, setPage, setRowsPerPage, resetPage }
}
